import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';

export const selectOptionsPeripheralMeasureTypes = (state: RootState) =>
  state.masters.peripheralsMeasureTypes.data.map((item: any) => ({
    value: item.id,
    label: item.description,
  }));

// Peripheral (device) code of a peripheral-measure-type code: "pc300_ecg" -> "pc300",
// "contourNext" -> "contourNext". Peripheral codes never contain "_", so the prefix is the device. The
// backends derive the peripheral the same way, so this is the matching key for rule exclusions.
const peripheralCodeOf = (measureTypeCode: string): string => {
  const code = measureTypeCode ?? '';
  const idx = code.indexOf('_');
  return idx > 0 ? code.slice(0, idx) : code;
};

// System pseudo-peripherals (internally generated measures, not real devices) — kept out of the selector.
const HIDDEN_PERIPHERAL_CODES = ['accumulate', 'generatedMeasure'];

// Options for the measure-rule "not evaluate / not accumulate" lists: ONE entry PER PERIPHERAL (device),
// keyed by the peripheral code (portable). genericMeasureTypeIds aggregates the generics of all the
// peripheral's measure types, so the editors can show only peripherals relevant to the rule's type.
export const selectOptionsRulePeripherals = (state: RootState) => {
  const byCode: Record<string, { value: string; label: string; genericMeasureTypeIds: number[] }> = {};

  (state.masters.peripheralsMeasureTypes.data || []).forEach((item: any) => {
    const code = peripheralCodeOf(item.code);
    if (HIDDEN_PERIPHERAL_CODES.includes(code)) return;

    // Label is the peripheral code itself (the device identifier, e.g. "pc300", "SGW-7"); peripheral
    // descriptions are not unique (several devices share "BP and Oximeter") and read like measure
    // categories, not devices.
    if (!byCode[code]) {
      byCode[code] = { value: code, label: code, genericMeasureTypeIds: [] };
    }
    (item.genericMeasureTypeIds || []).forEach((id: any) => {
      if (!byCode[code].genericMeasureTypeIds.includes(id)) byCode[code].genericMeasureTypeIds.push(id);
    });
  });

  return Object.values(byCode).sort((a, b) =>
    String(a.label).localeCompare(String(b.label), 'es', { sensitivity: 'base' }),
  );
};

export const selectOptionsPeripherals = (state: RootState) => {
  const typeLabels: Record<string, string> = Object.fromEntries(
    state.masters.peripheralsTypes.data.map((t: any) => [t.code, t.label ?? t.code])
  );
  return state.masters.peripherals.data
    .map((item: any) => ({
      value: item.id,
      label: item.code,
      peripheralMeasures: typeLabels[item.code] ?? item.code,
    }))
    .sort((a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
    );
};

export const selectResolutionLabelByCode = (state: RootState): Record<string, string> => {
  const all = [
    ...(state.masters.resolutions?.notArchived?.data ?? []),
    ...(state.masters.resolutions?.archived?.data ?? []),
  ];
  return Object.fromEntries(all.map((r: any) => [r.code, r.label]));
};

export const selectServiceStateLabelByCode = (state: RootState): Record<string, string> => {
  const all: any[] = state.masters.serviceStates?.data ?? [];
  return Object.fromEntries(all.map((s: any) => [s.code, s.label ?? s.code]));
};

export const selectServiceReasonLabelByCode = (state: RootState): Record<string, string> => {
  // serviceTypeReason data is already normalised by normalizeDataServiceReasonType,
  // so each item carries a `label` field (populated from catalog_translations).
  const all: any[] = state.masters.serviceTypeReason?.data ?? [];
  return Object.fromEntries(all.map((r: any) => [r.code, r.label ?? r.code]));
};

export const selectServiceTypeLabelByCode = (state: RootState): Record<string, string> => {
  const all: any[] = state.masters.serviceType?.data ?? [];
  return Object.fromEntries(all.map((t: any) => [t.code, t.label ?? t.code]));
};

export const selectSexLabelByCode = (state: RootState): Record<string, string> => {
  const all: any[] = state.masters.sexesTypes?.data ?? [];
  return Object.fromEntries(all.map((s: any) => [s.code, s.label ?? s.code]));
};

export const selectRelationshipLabelByCode = (state: RootState): Record<string, string> => {
  const all: any[] = state.masters.relationships?.data ?? [];
  return Object.fromEntries(all.map((r: any) => [r.code, r.label ?? r.code]));
};

export const selectMeasuresType = (state: RootState) => state.masters.measuresTypes.data;
export const selectOptionsMeasuresTypes = (state: RootState) =>
  state.masters.measuresTypes.data
    .map((item: any) => ({
      value: item.id,
      label: item.label ?? item.code,
    }))
    .sort((a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
    );

// Etiqueta traducida de cada tipo de medida por código, tal como la sirve el back (catálogo
// MEASURE_TYPES de CATALOG_TRANSLATIONS). Sólo códigos con traducción real: si el back devuelve
// el propio código, no entra y el llamador cae a su fallback (i18n local).
export const selectMeasureTypeLabelByCode = createSelector(
  (state: RootState) => state.masters.measuresTypes.data,
  (types: any[]): Record<string, string> =>
    Object.fromEntries(
      (types ?? []).filter((t) => t?.code && t.label && t.label !== t.code).map((t) => [t.code, t.label]),
    ),
);

export const selectMeasuresGenericTypes = (state: RootState) => state.masters.measuresGenericTypes.data;
export const selectOptionsMeasuresGenericTypes = (state: RootState) =>
  state.masters.measuresGenericTypes.data.map((item: any) => ({
    value: item.id,
    label: item.label ?? item.code,
  }));

export const selectMeasuresUnits = (state: RootState) => state.masters.measuresUnits.data;
export const selectOptionsMeasuresUnits = (state: RootState) =>
  state.masters.measuresUnits.data.map((item: any) => ({
    value: item.id,
    label: item.code,
  }));

export const selectOptionsDevicesTypes = (state: RootState) =>
  state.masters.devicesTypes.data.map((item: any) => ({
    value: item.id,
    label: item.code,
  }));

export const selectOptionsRaces = (state: RootState) =>
  state.masters.racesTypes.data.map((item: any) => ({
    value: item.id,
    label: item.label ?? item.code,
  }));
export const selectRacesTypes = (state: RootState) => state.masters.racesTypes.data;

export const selectOptionsSexes = (state: RootState) =>
  state.masters.sexesTypes.data.map((item: any) => ({
    value: item.id,
    label: item.label ?? item.code,
  }));
export const selectSexesTypes = (state: RootState) => state.masters.sexesTypes.data;

export const selectOptionsAgenda = (state: RootState) =>
  state.masters.agendaTypes.data.map((item: any) => ({
    value: item.id,
    label: item.name,
  }));
export const selectAgendaTypes = (state: RootState) => state.masters.agendaTypes.data;

export const selectMeasureStatus = (state: RootState) => state.masters.measuresStatus.data;
export const selectOptionsMeasureStatus = (state: RootState) =>
  state.masters.measuresStatus.data.map((item: any) => ({
    value: item.id,
    label: item.status,
  }));

export const selectOptionsResolutionsNotArchived = (state: RootState) => state.masters.resolutions?.notArchived?.data;

export const selectOptionsResolutionsArchived = (state: RootState) => state.masters.resolutions?.archived?.data;

export const selectOptionsResolutions = (state: RootState) => state.masters.resolutions;

export const selectPathologies = (state: RootState) => state.masters.pathologiesTypes.data;

export const selectOptionsPathologies = (state: RootState) =>
  state.masters.pathologiesTypes.data.map((item: any) => ({
    value: item.id,
    label: item.code,
  }));

export const selectMaritalStatusTypes = (state: RootState) => state.masters.maritalStatus.data;

export const selectOptionsMaritalStatus = (state: RootState) =>
  state.masters.maritalStatus.data.map((item: any) => ({
    value: item.id,
    label: item.label ?? item.code,
  }));

export const selectOptionsEducation = (state: RootState) =>
  state.masters.education.data.map((item: any) => ({
    value: item.id,
    label: item.label ?? item.code,
  }));

export const selectOptionsEthnicity = (state: RootState) =>
  state.masters.ethnicity.data.map((item: any) => ({
    value: item.id,
    label: item.label ?? item.code,
  }));

export const selectOptionsLanguage = (state: RootState) =>
  state.masters.language.data.map((item: any) => ({
    value: item.id,
    label: item.label ?? item.code,
  }));

// Puede estar sin cargar (null) fuera de la ficha del paciente, p.ej. en la pestaña de servicios del grupo.
export const selectOptionshealthCenter = (state: RootState) =>
  (state.masters.healthCenter.data ?? []).map((item: any) => ({
    value: item.id,
    label: item.code,
  }));

export const selectOptionsRelationships = (state: RootState) =>
  state.masters.relationships.data.map((item: any) => ({
    value: item.id,
    label: item.label ?? item.code,
    code: item.code,
  }));

export const selectRelationshipsTypes = (state: RootState) => state.masters.relationships.data;

export const selectServiceTypes = (state: RootState) => state.masters.serviceType.data;

export const selectServiceReasonTypes = (state: RootState) => state.masters.serviceTypeReason.data;

export const selectSettingsLanguages = (state: RootState) => state.masters.settingsLanguages.data;
export const selectSettingsLanguagesOptions = (state: RootState) => {
  const items = state.masters.settingsLanguages?.data ?? [];

  return items.map((item: any) => ({
    value: item.id,
    label: item.code,
  }));
};

export const selectSettingsMeasurementUnits = (state: RootState) => state.masters.settingsMeasurementUnits.data;
export const selectSettingsMeasurementUnitsOptions = (state: RootState) => {
  const items = state.masters.settingsMeasurementUnits?.data ?? [];

  return items.map((item: any) => ({
    value: item.id,
    label: item.code,
  }));
};

export const selectSettingsDateFormat = (state: RootState) => state.masters.settingsDateFormat.data;
export const selectSettingsDateFormatOptions = (state: RootState) => {
  const items = state.masters.settingsDateFormat?.data ?? [];

  return items.map((item: any) => ({
    value: item.id,
    label: item.code,
  }));
};
